/* Inhalt des Profil-PDFs (Stufe 4) — reine Funktionen, kein DOM, keine Texte in Oberflächensprache.
 *
 * DATENSCHUTZ: Das Profilblatt geht an externe Empfänger und ist für jede Rolle gleich. Grundsatz:
 * "Felder der öffentlichen Ansicht plus Material (Highlight, Matches)".
 *  - Öffentliche Ansicht (domain/playerPublic.js PUBLIC_COLUMNS): Name, Position und zweite Position, Geburtsdatum
 *    (sonst nur Jahrgang), Größe, starker Fuß, Nationalität, Nationalmannschaft mit Stufe, aktueller Verein mit Logo,
 *    "At Grassrunners since", Foto, Highlight-Video, Transfermarkt-Link.
 *  - Material: die Matches des Reiters Material (`videos`). Das ist eine BEWUSSTE Erweiterung (Entscheidung Bastian,
 *    2026-10-14), kein Versehen. Von jedem Match werden nur title, url, opponent, shirtNumber, shirtColor und stats
 *    gelesen (MATCH_KEYS); jeder andere Schlüssel am Match landet nicht im PDF. Gäste bekommen die Matches und die
 *    Schlagwörter seit F4 über die Spalten match_videos und traits der Ansicht players_public (supabase/gaeste-material.sql,
 *    dort schon auf diese Schlüssel beschränkt) und laden die Karte im Reiter Material herunter; Markier-Modus und
 *    Listen-Export bleiben für Gäste gesperrt.
 *  - Transferhistorie (F13): nur bei Status "transferred" und nur, wenn Einträge übergeben werden (dieselben Zeilen wie im
 *    Reiter Transfer Info, siehe composables/useTransfers.js). Aus jeder Zeile nur Art, Datum, abgebender und aufnehmender
 *    Verein mit Logo (TRANSFER_KEYS); Notiz, Ids und Anlegedaten kommen nicht ins PDF. Gebühren gibt es in diesen Zeilen nicht.
 *  - Nicht dabei: Dokumente, Pass, Visa, Verträge, Reisen, Newsfeed, Summen, Dokumentstatus, Spieler-ID. Der Status wird nur
 *    gelesen, um die Transferhistorie zu entscheiden, und steht nicht im Ergebnis.
 *  - Player Card (D2): dazu die Schlagwörter (`traits`, höchstens drei). Trikotnummer, Saison und die Heatmap-Vorlage
 *    werden abgeleitet (aus den Matches, dem Datum, der ersten und zweiten Position), nicht erfasst.
 *
 * Die Felder werden einzeln aus einer festen Liste gelesen (PROFILE_FIELDS), nicht aus dem Objekt kopiert.
 * Jedes weitere Feld am Spielerobjekt (docs, visas, trips, contract …) kann daher nicht im PDF landen; der Test
 * in profileSheet.test.js hält das fest.
 */

import { playerClub, transferClub } from './footballClub.js';
import { sortTransfers, TRANSFER_KINDS } from './transfers.js';
import { birthYearOf, validDob } from './birthYear.js';
import { photoPathFor } from './playerPublic.js';
import { fmtDate, initials, capitalizeFirst } from './format.js';
import { deriveShirtNumber, seasonLabel } from './playerCard.js';
import { normalizeTraits, keywordLabelEn } from './playerKeywords.js';
import { heatSpots } from './heatZones.js';

/** Die einzigen Felder des Spielerobjekts, die das Profilblatt liest. */
export const PROFILE_FIELDS = [
  'firstName', 'lastName', 'pos', 'pos2', 'dob', 'jg', 'height', 'foot', 'nationality',
  'nationalTeam', 'ntLevel', 'currentClub', 'currentClubId', 'currentClubLogoPath',
  'atGrassrunnersSince', 'avatarPath', 'bioPhotoPath', 'highlightVideo', 'transfermarktUrl',
  'videos',
  /* Schlagwörter der Player Card (D2): höchstens drei Texte, frei wählbar */
  'traits',
];

/** Die einzigen Schlüssel eines Matches, die das Profilblatt liest. */
export const MATCH_KEYS = ['title', 'url', 'opponent', 'shirtNumber', 'shirtColor', 'stats'];
/** Höchstens so viele Matches im Blatt; weitere werden als "+ n more" gezählt (es bleibt ein Blatt je Spieler). */
export const MAX_MATCHES = 8;

const PLACEHOLDER_POSITION = 'Position TBD';

const clean = (v) => (v == null ? '' : String(v).replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim());

/** Nur http(s)-Adressen ohne Leerraum; alles andere (javascript:, data:, Freitext) ist kein Link. */
export function safeUrl(value) {
  const s = clean(value);
  if (s.length > 500 || !/^https?:\/\/[^\s]+$/i.test(s)) return '';
  return s;
}

/** Die einzigen Schlüssel einer Transferzeile, die das Profilblatt liest (Notiz, Id, Anlegedatum bleiben draußen). */
export const TRANSFER_KEYS = [
  'date', 'kind', 'fromClub', 'fromClubLogoPath', 'fromClubId', 'toClub', 'toClubLogoPath', 'toClubId', 'createdAt',
];

/**
 * Transferzeilen -> Einträge der Karte, neueste zuerst. Leere Liste oder nicht transferiert: null.
 * Vereine über das Register (Verweis), Rückfall auf den Text — wie im Reiter Transfer Info (TransferHistory.vue).
 */
function buildTransfers(rows, status, clubsById) {
  if (status !== 'transferred' || !Array.isArray(rows) || !rows.length) return null;
  const picked = rows.filter((r) => r && typeof r === 'object').map((r) => {
    const t = {};
    for (const key of TRANSFER_KEYS) t[key] = r[key];
    return t;
  });
  const side = (row, which) => {
    const c = transferClub(clubsById, row, which);
    return { name: clean(c && c.name), logoPath: (c && c.logoPath) || null };
  };
  const out = sortTransfers(picked).map((row) => ({
    kind: TRANSFER_KINDS.includes(row.kind) ? row.kind : 'permanent',
    date: fmtDate(clean(row.date)),
    from: side(row, 'from'),
    to: side(row, 'to'),
  }));
  return out.length ? out : null;
}

/** Nur die erlaubten Felder des Spielerobjekts. */
export function pickProfileFields(player) {
  const out = {};
  const src = player || {};
  for (const key of PROFILE_FIELDS) out[key] = src[key];
  return out;
}

function heightText(h) {
  const s = clean(h);
  return /^\d{2,3}$/.test(s) ? `${s} cm` : s;
}

/** Ein Match aus den erlaubten Schlüsseln; null, wenn weder Titel noch gültige Adresse da sind. */
function buildMatch(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const m = {};
  for (const key of MATCH_KEYS) m[key] = raw[key];
  const url = safeUrl(m.url);
  const title = clean(m.title);
  if (!title && !url) return null;
  const opponent = clean(m.opponent);
  /* Zeile unter dem Gegner: Nummer, Farbe, Stats (nur, was belegt ist) */
  const meta = [
    clean(m.shirtNumber) ? `#${clean(m.shirtNumber).replace(/^#/, '')}` : '',
    clean(m.shirtColor),
    clean(m.stats),
  ].filter(Boolean).join(' · ');
  const detail = [opponent ? `vs. ${opponent}` : '', meta].filter(Boolean).join(' · ');
  return {
    title: title || 'Match',
    url,
    opponent,
    shirtNumber: clean(m.shirtNumber).replace(/^#/, ''),
    shirtColor: clean(m.shirtColor),
    meta,
    detail,
  };
}

function bornText(p) {
  const dob = validDob(p.dob);
  if (dob) return fmtDate(dob);
  return birthYearOf(p);
}

const GRASSRUNNERS_NAME = 'Grassrunners FC';

/** "2021-09-01" -> "09.2021"; nur ein Jahr bleibt das Jahr; Unlesbares ergibt leer. */
function sinceMonth(value) {
  const m = /^(\d{4})-(\d{2})/.exec(value);
  if (m) return `${m[2]}.${m[1]}`;
  return /^\d{4}$/.test(value) ? value : '';
}

/**
 * Das Profilblatt eines Spielers als Daten.
 *
 * @param {object} player
 * @param {{ clubsById?: Map|null }} [opts]  Klubregister (Name und Logo über den Verweis, Rückfall auf den Text)
 * @returns {{
 *   firstName: string, lastName: string, fullName: string, initials: string, position: string,
 *   positions: string[], traits: string[], shirtNumber: string, season: string,
 *   heat: Array<{ zone: string, x: number, y: number, strength: number }>, birthYear: string,
 *   facts: Array<{ key: string, value: string, logoPath?: string|null, since?: string, fallback?: boolean }>,
 *   links: Array<{ key: 'transfermarkt', url: string }>,
 *   material: null | { highlight: { title: string, url: string }|null,
 *                      matches: Array<{ title: string, url: string, opponent: string, meta: string, detail: string }>,
 *                      more: number },
 *   transfers: null | Array<{ kind: 'permanent'|'loan'|'loan_end', date: string,
 *                             from: { name: string, logoPath: string|null }, to: { name: string, logoPath: string|null } }>,
 *   photoPath: string|null, clubLogoPath: string|null, clubFallback: boolean
 * }}
 */
export function buildProfile(player, { clubsById = null, now = new Date(), transfers = null } = {}) {
  const p = pickProfileFields(player);
  const firstName = clean(p.firstName);
  const lastName = clean(p.lastName);
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  const pos = clean(p.pos) === PLACEHOLDER_POSITION ? '' : clean(p.pos);
  const positions = [pos, clean(p.pos2)].filter(Boolean);
  const position = positions.join(' · ');

  const club = playerClub(clubsById, p);
  const clubName = clean(club && club.name);
  const clubLogoPath = (club && club.logoPath) || null;

  const team = clean(p.nationalTeam);
  const level = clean(p.ntLevel);
  /* Club und Since teilen sich EINE Kachel: Vereinsname, darunter klein "since MM.JJJJ". Ohne Vereinseintrag, aber mit
     Since ist der Club "Grassrunners FC" (Wappen aus der App, siehe clubFallback). Ohne beides keine Club-Kachel. */
  const since = sinceMonth(clean(p.atGrassrunnersSince));
  const clubFallback = !clubName && !!since;
  const clubShown = clubName || (clubFallback ? GRASSRUNNERS_NAME : '');

  const shirtNumber = deriveShirtNumber(p.videos);
  const season = seasonLabel(now);
  /* Nur das Geburtsdatum, KEIN Alter: Das Alter ändert sich, das Dokument bleibt. */
  const bornValue = bornText(p);

  /* Reihenfolge = Reihenfolge im PDF; fehlende Werte entfallen (nie "undefined" oder "null") */
  const facts = [
    { key: 'born', value: bornValue },
    { key: 'height', value: heightText(p.height) },
    { key: 'foot', value: capitalizeFirst(clean(p.foot)) },
    { key: 'nationality', value: clean(p.nationality) },
    { key: 'nationalTeam', value: team ? (level ? `${team} · ${level}` : team) : '' },
    { key: 'club', value: clubShown, logoPath: clubName ? clubLogoPath : null, since: clubShown ? since : '', fallback: clubFallback },
    { key: 'shirt', value: shirtNumber ? `#${shirtNumber} · ${season}` : '' },
  ].filter((f) => f.value);

  /* Profil-Links (Transfermarkt); der Highlight steht im Abschnitt Material */
  const links = [];
  const tm = safeUrl(p.transfermarktUrl);
  if (tm) links.push({ key: 'transfermarkt', url: tm });

  /* Material: Highlight und Matches in der Reihenfolge des Reiters; ohne beides kein Abschnitt */
  const hvRaw = p.highlightVideo && typeof p.highlightVideo === 'object' ? p.highlightVideo : null;
  const hvUrl = hvRaw ? safeUrl(hvRaw.url) : '';
  const hvTitle = hvRaw ? clean(hvRaw.title) : '';
  const highlight = hvUrl || hvTitle ? { title: hvTitle, url: hvUrl } : null;
  const allMatches = (Array.isArray(p.videos) ? p.videos : []).map(buildMatch).filter(Boolean);
  const matches = allMatches.slice(0, MAX_MATCHES);
  const material = highlight || matches.length
    ? { highlight, matches, more: allMatches.length - matches.length }
    : null;

  return {
    firstName,
    lastName,
    fullName,
    initials: initials(fullName),
    position,
    positions,
    /* Anzeigenamen immer Englisch (das PDF ist englisch); Altwerte unverändert */
    traits: normalizeTraits(p.traits).map(keywordLabelEn),
    shirtNumber,
    season,
    heat: heatSpots(pos, clean(p.pos2)),
    birthYear: birthYearOf(p),
    facts,
    links,
    material,
    transfers: buildTransfers(transfers, player && player.status, clubsById),
    photoPath: photoPathFor(p),
    clubLogoPath: clubName ? clubLogoPath : null,
    clubFallback,
  };
}
